import {Common, CommonCommon, RouteOptimization} from '@priority-software/priority-geoservice-api';
import {Field, Form, formsStore, Record} from '../../../../stores/Form';
import {OptimizationDialogConsiderOption, OptimizationDialogState} from './index';
import {
    loadSubformsForSpecificRows,
    openDeliveryGroupsForm,
    openLorriesForm,
    openLorryTypesForm
} from '../../actions/action';
import {Dictionary} from '../../../EOYWizard/types';
import {
    CAPABILITY,
    ColumnsNamesForDeliveryCapabilitiesForGroups,
    ColumnsNamesForDeliveryDistributionRoutesForGroups,
    ColumnsNamesForDeliveryGroups,
    ColumnsNamesForDeliveryZonesForGroups,
    ColumnsNamesForLocationsSubform,
    ColumnsNamesForLorries,
    ColumnsNamesForLorriesSubform,
    ColumnsNamesForLorryTypes,
    ColumnsNamesForRoutes,
    DEFAULTDISTRLINE,
    DISTRLINE,
    DLVCAPABILITYGROUPS,
    DLVDISTRLINEGROUPS,
    DLVZONEGROUPS,
    DOCGUID,
    FROMTIME,
    GPSX,
    GPSY,
    NWEIGHT,
    TOTIME,
    UNLOADTIME,
    VOLUME,
    WEIGHT,
    ZONE
} from '../../constants';
import {closeFormBySession} from '../../../Form/actions/form';
import {getLang} from '../../../../Localization';
import {getStringFormatByColumnType} from '../../../Form/utils/date';
import {priority} from '@priority-software/priority-proto-api/lib';
import moment from 'moment/moment';
import _ from 'lodash';
import {LocationType, RouteType} from '../../types';
import {showErrorMessage} from '../../../../_shared/Messages/actions/MessageActions';
import {texts} from '../../texts';
import {onHyperlinkClick} from '../../../../services/webSdk/proc';

export type RequestData = {
    locations: Common.Location[];
    jobs: RouteOptimization.Job[];
    vehicles: RouteOptimization.Vehicle[];
    options: RouteOptimization.Options;
};

const checkLocation = (location: Common.Location) =>
    _.isFinite(location.getLatitude()) &&
    _.isFinite(location.getLongitude()) &&
    !!location.getLongitude() &&
    !!location.getLatitude();

const showMissingLocationMessage = async (isUnassigned?: boolean) => {
    await showErrorMessage({
        content:
            texts[
                isUnassigned
                    ? 'optimization_no_coordinates_issue'
                    : 'optimization_no_coordinates_warning'
            ],
        confirmButtonText: texts['ok_button'],
        onConfirm: () => {},
        onClose: () => {}
    });
    throw new Error();
};

const showMissingLorriesMessage = async () => {
    const content: string = texts['optimization_no_trucks_issue'];
    const match = content.match(/"[^"]+"/);
    await showErrorMessage({
        content,
        confirmButtonText: texts['ok_button'],
        onConfirm: () => {},
        onClose: () => {},
        linkData:
            match && match[0]
                ? [
                      {
                          ename: 'OPTDELIVERYORDERERR',
                          from: match.index!,
                          len: match[0].length,
                          linkData: {type: 'F', ename: 'LORRIES'},
                          title: match[0],
                          type: 'F'
                      }
                  ]
                : undefined,
        onLinkClick: onHyperlinkClick
    });
    throw new Error();
};

const addTimeWindowAndServiceToJob = (
    job: RouteOptimization.Job,
    date: Field,
    fromTime: Field,
    toTime: Field,
    unloadTime: Field
) => {
    const isDataExists = [
        toTime.Value.Time.seconds,
        toTime.Value.Time.minutes,
        toTime.Value.Time.hours,
        fromTime.Value.Time.seconds,
        fromTime.Value.Time.minutes,
        fromTime.Value.Time.hours
    ].some(x => x !== 0);

    if (isDataExists) {
        const timeWindows = new RouteOptimization.TimeWindowList();

        const timeWindow = new RouteOptimization.TimeWindow();

        timeWindow.setStart(
            moment({
                year: date.Value.Date.year,
                month: date.Value.Date.month - 1,
                date: date.Value.Date.day,
                hours: fromTime.Value.Time.hours || 0,
                minutes: fromTime.Value.Time.minutes || 0,
                seconds: fromTime.Value.Time.seconds || 0
            }).unix()
        );

        timeWindow.setEnd(
            moment({
                year: date.Value.Date.year,
                month: date.Value.Date.month - 1,
                date: date.Value.Date.day,
                hours: toTime.Value.Time.hours || 0,
                minutes: toTime.Value.Time.minutes || 0,
                seconds: toTime.Value.Time.seconds || 0
            }).unix()
        );

        timeWindows.addTimewindows(timeWindow);

        job.setTimewindows(timeWindows);
    }

    job.setService(
        unloadTime.Value.Time.seconds +
            unloadTime.Value.Time.minutes * 60 +
            unloadTime.Value.Time.hours * 60 * 60
    );
};

export const generateUnassignedRequestData = async (
    selectedPlannedRows: Record[],
    deliveryDateValue: string,
    optimizationDialogState: OptimizationDialogState,
    dname: string
): Promise<RequestData> => {
    const locations: Common.Location[] = [];
    const jobs: RouteOptimization.Job[] = [];
    const vehicles: RouteOptimization.Vehicle[] = [];
    const options = new RouteOptimization.Options();

    const plannedForm = selectedPlannedRows[0].parentForm;
    const plannedFormColumns = selectedPlannedRows[0].parentForm.columns;

    formsStore.setCurrentSession(plannedForm.session);

    const GPSX_id = plannedFormColumns.find(x => x.Name === GPSX).id;
    const GPSY_id = plannedFormColumns.find(x => x.Name === GPSY).id;

    // validate all selected rows has coordinates
    for (const row of selectedPlannedRows) {
        const location = new Common.Location();
        location.setLatitude(Number.parseFloat(row.getField(GPSX_id).currentValue as string));
        location.setLongitude(Number.parseFloat(row.getField(GPSY_id).currentValue as string));

        if (!checkLocation(location)) {
            await showMissingLocationMessage(true);
        }
    }

    // Load LORRIES with subforms
    const lorriesForm = await openLorriesForm(dname);
    const lorriesRecords = lorriesForm.records;

    if (lorriesRecords.filter(x => !x.isNew).length === 0) {
        await showMissingLorriesMessage();
    }

    const lorriesSubforms = await loadSubformsForSpecificRows(
        lorriesForm,
        [DEFAULTDISTRLINE],
        lorriesRecords.filter(x => !x.isNew).map(x => x.recordIndex)
    );

    const lorrySubformRecord: Dictionary<Record> = {};

    for (const lorriesRecordIndex of Object.keys(lorriesSubforms)) {
        const recordIndex = Number.parseInt(lorriesRecordIndex);
        const subformRecords = lorriesSubforms[lorriesRecordIndex][DEFAULTDISTRLINE].records.filter(
            x => !x.isNew
        );
        if (subformRecords.length > 0) {
            lorrySubformRecord[recordIndex] = subformRecords[0];
        }
    }

    await closeFormBySession(lorriesForm?.session, false);

    const LTCODE_id = lorriesForm.getColumnByName(ColumnsNamesForLorries.LTCODE).id;
    const LTCODE_unique = Array.from(
        new Set(lorriesRecords.map(x => x.getField(LTCODE_id).currentValue as string))
    );

    formsStore.setCurrentSession(plannedForm.session);

    // Load LORRYTYPES
    const lorryTypesForm = await openLorryTypesForm(LTCODE_unique, dname);
    const lorryTypesRecords = lorryTypesForm.records.filter(x => !x.isNew);
    await closeFormBySession(lorryTypesForm?.session, false);

    // Create locations array
    const locationsVehicle: Common.Location[] = [];
    const locationsDelivery: Common.Location[] = [];

    for (let i = 0; i < lorriesRecords.length; ++i) {
        if (lorrySubformRecord[lorriesRecords[i].recordIndex]) {
            const startVehicleLocation = new Common.Location();
            const parentForm = lorrySubformRecord[lorriesRecords[i].recordIndex].parentForm;

            const SGPSX_id = parentForm.getColumnByName(ColumnsNamesForLorriesSubform.SGPSX).id;
            const SGPSY_id = parentForm.getColumnByName(ColumnsNamesForLorriesSubform.SGPSY).id;
            const EGPSX_id = parentForm.getColumnByName(ColumnsNamesForLorriesSubform.EGPSX).id;
            const EGPSY_id = parentForm.getColumnByName(ColumnsNamesForLorriesSubform.EGPSY).id;

            startVehicleLocation.setLatitude(
                Number.parseFloat(
                    lorrySubformRecord[lorriesRecords[i].recordIndex].getField(SGPSX_id)
                        .currentValue as string
                )
            );
            startVehicleLocation.setLongitude(
                Number.parseFloat(
                    lorrySubformRecord[lorriesRecords[i].recordIndex].getField(SGPSY_id)
                        .currentValue as string
                )
            );

            if (
                !locationsVehicle.find(
                    x =>
                        x.getLatitude() === startVehicleLocation.getLatitude() &&
                        x.getLongitude() === startVehicleLocation.getLongitude()
                )
            ) {
                if (checkLocation(startVehicleLocation)) {
                    locationsVehicle.push(startVehicleLocation);
                }
            }

            const endVehicleLocation = new Common.Location();

            endVehicleLocation.setLatitude(
                Number.parseFloat(
                    lorrySubformRecord[lorriesRecords[i].recordIndex].getField(EGPSX_id)
                        .currentValue as string
                )
            );
            endVehicleLocation.setLongitude(
                Number.parseFloat(
                    lorrySubformRecord[lorriesRecords[i].recordIndex].getField(EGPSY_id)
                        .currentValue as string
                )
            );

            if (
                !locationsVehicle.find(
                    x =>
                        x.getLatitude() === endVehicleLocation.getLatitude() &&
                        x.getLongitude() === endVehicleLocation.getLongitude()
                )
            ) {
                if (checkLocation(endVehicleLocation)) {
                    locationsVehicle.push(endVehicleLocation);
                }
            }
        }
    }

    const DOCGUID_id = plannedFormColumns.find(x => x.Name === DOCGUID).id;
    const UNLOADTIME_id = plannedFormColumns.find(x => x.Name === UNLOADTIME).id;
    const FROMTIME_id = plannedFormColumns.find(x => x.Name === FROMTIME).id;
    const TOTIME_id = plannedFormColumns.find(x => x.Name === TOTIME).id;
    const ZONE_id = plannedFormColumns.find(x => x.Name === ZONE).id;
    const DISTRLINE_id = plannedFormColumns.find(x => x.Name === DISTRLINE).id;
    const CAPABILITY_id = plannedFormColumns.find(x => x.Name === CAPABILITY).id;
    const VOLUME_id = plannedFormColumns.find(x => x.Name === VOLUME).id;
    const WEIGHT_id = plannedFormColumns.find(x => x.Name === WEIGHT).id;
    const NWEIGHT_id = plannedFormColumns.find(x => x.Name === NWEIGHT).id;

    for (const row of selectedPlannedRows) {
        const location = new Common.Location();
        location.setLatitude(Number.parseFloat(row.getField(GPSX_id).currentValue as string));
        location.setLongitude(Number.parseFloat(row.getField(GPSY_id).currentValue as string));

        if (
            !locationsDelivery.find(
                x =>
                    x.getLatitude() === location.getLatitude() &&
                    x.getLongitude() === location.getLongitude()
            )
        ) {
            if (checkLocation(location)) {
                locationsDelivery.push(location);
            }
        }
    }

    locations.push(...locationsVehicle);

    for (const location of locationsDelivery) {
        if (
            !locations.find(
                x =>
                    x.getLatitude() === location.getLatitude() &&
                    x.getLongitude() === location.getLongitude()
            )
        ) {
            locations.push(location);
        }
    }

    // Create jobs array
    const appLang = getLang();
    const format = getStringFormatByColumnType(priority.netitems.form.ColumnType.Date8, appLang);
    const momentDate = moment(deliveryDateValue, format);

    jobs.push(
        ...(selectedPlannedRows
            .map(x => {
                const job = new RouteOptimization.Job();
                job.setId(x.getField(DOCGUID_id).currentValue as string);

                const lat = Number.parseFloat(x.getField(GPSX_id).currentValue as string);
                const lng = Number.parseFloat(x.getField(GPSY_id).currentValue as string);
                const locationIndex = locations.findIndex(
                    y => y.getLongitude() === lng && y.getLatitude() === lat
                );

                if (locationIndex === -1) {
                    return false;
                }

                job.setLocationindex(locationIndex);

                if (optimizationDialogState.timeWindowConsideration) {
                    const date = {
                        Value: {
                            Date: {
                                year: momentDate.get('year'),
                                month: momentDate.get('month') + 1,
                                day: momentDate.get('date')
                            }
                        }
                    } as any;
                    const fromTime = x.getField(FROMTIME_id);
                    const toTime = x.getField(TOTIME_id);
                    const unloadTime = x.getField(UNLOADTIME_id);

                    addTimeWindowAndServiceToJob(job, date, fromTime, toTime, unloadTime);
                }

                const zoneList = new RouteOptimization.ZoneList();

                const zones: number[] = [];

                switch (optimizationDialogState.consider) {
                    case OptimizationDialogConsiderOption.Area:
                        const ZONE_value = x.getField(ZONE_id).Value.Int as number;
                        if (ZONE_value) {
                            zones.push(ZONE_value);
                        } else {
                            zones.push(0);
                        }
                        break;
                    case OptimizationDialogConsiderOption.Route:
                        const DISTRLINE_value = x.getField(DISTRLINE_id).Value.Int as number;
                        if (DISTRLINE_value) {
                            zones.push(DISTRLINE_value);
                        } else {
                            zones.push(0);
                        }
                        break;
                }

                if (zones.length > 0) {
                    for (let i = 0; i < zones.length; i++) {
                        zoneList.addZones(zones[i], i);
                    }
                }

                job.setZones(zoneList);

                const skillList = new RouteOptimization.SkillList();
                const skill = Number.parseInt(x.getField(CAPABILITY_id).currentValue as string);

                if (_.isFinite(skill) && optimizationDialogState.constraintsConsideration) {
                    skillList.addSkills(skill);
                }

                job.setSkills(skillList);

                const deliveryList = new RouteOptimization.DeliveryList();

                const delivery = new RouteOptimization.Delivery();

                const weight = Math.round(x.getField(WEIGHT_id)?.Value?.Real);
                const nweight = Math.round(x.getField(NWEIGHT_id)?.Value?.Real);
                const volume = Math.round(x.getField(VOLUME_id)?.Value?.Real);

                if (weight > 0) {
                    delivery.setWeight(weight);
                } else {
                    delivery.setWeight(nweight);
                }

                if (_.isFinite(volume)) {
                    delivery.setVolume(volume);
                }

                if (_.isFinite(delivery.getWeight()) && _.isFinite(delivery.getVolume())) {
                    deliveryList.addDeliveries(delivery, 0);
                }

                job.setDeliveries(deliveryList);

                return job;
            })
            .filter(Boolean) as RouteOptimization.Job[])
    );

    const DLVGROUPCODE_id = lorriesForm.getColumnByName(ColumnsNamesForLorries.DLVGROUPCODE).id;

    const DLVGROUPCODE_unique = Array.from(
        new Set(
            lorriesRecords
                .filter(x => !x.isNew)
                .map(x => x.getField(DLVGROUPCODE_id).currentValue as string)
        )
    );

    formsStore.setCurrentSession(plannedForm.session);

    // Load DLVGROUPS with subforms
    const deliveryGroupsForm = await openDeliveryGroupsForm(DLVGROUPCODE_unique, dname);
    const deliveryGroupsRecords = deliveryGroupsForm.records;

    const DLVGROUPCODE_dg_id = deliveryGroupsForm.getColumnByName(
        ColumnsNamesForDeliveryGroups.DLVGROUPCODE
    ).id;
    const ALLZONES_id = deliveryGroupsForm.getColumnByName(
        ColumnsNamesForDeliveryGroups.ALLZONES
    ).id;
    const ALLROUTES_id = deliveryGroupsForm.getColumnByName(
        ColumnsNamesForDeliveryGroups.ALLROUTES
    ).id;
    const ALLCAPABILITIES_id = deliveryGroupsForm.getColumnByName(
        ColumnsNamesForDeliveryGroups.ALLCAPABILITIES
    ).id;

    const deliveryGroupsSubforms = await loadSubformsForSpecificRows(
        deliveryGroupsForm,
        [DLVCAPABILITYGROUPS, DLVZONEGROUPS, DLVDISTRLINEGROUPS],
        deliveryGroupsRecords.filter(x => !x.isNew).map(x => x.recordIndex),
        true,
        subform => {
            switch (subform) {
                case DLVDISTRLINEGROUPS:
                    return [DISTRLINE];
                case DLVZONEGROUPS:
                    return [ZONE];
                default:
                    return undefined;
            }
        }
    );

    await closeFormBySession(deliveryGroupsForm?.session, false);

    // Create vehicles array
    vehicles.push(
        ...(lorriesRecords
            .filter(x => !x.isNew)
            .filter(x => {
                const OPTIMIZATION_id = x.parentForm.getColumnByName(
                    ColumnsNamesForLorries.OPTIMIZATION
                ).id;
                return !!x.getField(OPTIMIZATION_id).Value.Boolean;
            })
            .map(x => {
                const vehicle = new RouteOptimization.Vehicle();
                const LORRYNUM_id = x.parentForm.getColumnByName(
                    ColumnsNamesForLorries.LORRYNUM
                ).id;
                vehicle.setId(x.getField(LORRYNUM_id).currentValue as string);

                if (!lorrySubformRecord[x.recordIndex]) {
                    return false;
                }

                const parentLorryForm = lorrySubformRecord[x.recordIndex].parentForm;
                const SGPSX_id = parentLorryForm.getColumnByName(
                    ColumnsNamesForLorriesSubform.SGPSX
                ).id;
                const SGPSY_id = parentLorryForm.getColumnByName(
                    ColumnsNamesForLorriesSubform.SGPSY
                ).id;
                const EGPSX_id = parentLorryForm.getColumnByName(
                    ColumnsNamesForLorriesSubform.EGPSX
                ).id;
                const EGPSY_id = parentLorryForm.getColumnByName(
                    ColumnsNamesForLorriesSubform.EGPSY
                ).id;

                const startLocationIndex = locations.findIndex(
                    y =>
                        y.getLongitude() ===
                            Number.parseFloat(
                                lorrySubformRecord[x.recordIndex].getField(SGPSY_id)
                                    .currentValue as string
                            ) &&
                        y.getLatitude() ===
                            Number.parseFloat(
                                lorrySubformRecord[x.recordIndex].getField(SGPSX_id)
                                    .currentValue as string
                            )
                );
                const endLocationIndex = locations.findIndex(
                    y =>
                        y.getLongitude() ===
                            Number.parseFloat(
                                lorrySubformRecord[x.recordIndex].getField(EGPSY_id)
                                    .currentValue as string
                            ) &&
                        y.getLatitude() ===
                            Number.parseFloat(
                                lorrySubformRecord[x.recordIndex].getField(EGPSX_id)
                                    .currentValue as string
                            )
                );

                if (startLocationIndex !== -1) {
                    vehicle.setStartindex(startLocationIndex);
                }
                if (endLocationIndex !== -1) {
                    vehicle.setEndindex(endLocationIndex);
                }

                const date = new CommonCommon.Date();

                date.setDay(momentDate.get('date'));
                date.setMonth(momentDate.get('month') + 1);
                date.setYear(momentDate.get('year'));
                vehicle.setDate(date);

                const capacityList = new RouteOptimization.CapacityList();

                const LTCODE_lorries_id = x.parentForm.getColumnByName(
                    ColumnsNamesForLorries.LTCODE
                ).id;

                const DLVGROUPCODE_lorries_id = x.parentForm.getColumnByName(
                    ColumnsNamesForLorries.DLVGROUPCODE
                ).id;

                const LTCODE_lorrytypes_id = lorryTypesForm.getColumnByName(
                    ColumnsNamesForLorryTypes.LTCODE
                ).id;

                const DLVGROUPCODE = x.getField(DLVGROUPCODE_lorries_id).currentValue as string;
                const LTCODE = x.getField(LTCODE_lorries_id).currentValue as string;

                const lorryTypeRecord = lorryTypesRecords.find(
                    y => (y.getField(LTCODE_lorrytypes_id).currentValue as string) === LTCODE
                );

                if (lorryTypeRecord) {
                    const LTWEIGHT_id = lorryTypesForm.getColumnByName(
                        ColumnsNamesForLorryTypes.LTWEIGHT
                    ).id;

                    const LTVOLUME_id = lorryTypesForm.getColumnByName(
                        ColumnsNamesForLorryTypes.LTVOLUME
                    ).id;

                    capacityList.addCapacities(
                        lorryTypeRecord.getField(LTWEIGHT_id).Value.Shifted as number,
                        0
                    );
                    capacityList.addCapacities(
                        lorryTypeRecord.getField(LTVOLUME_id).Value.Shifted as number,
                        1
                    );
                }

                vehicle.setCapacity(capacityList);

                const zoneList = new RouteOptimization.ZoneList();

                const deliveryGroupRecord = deliveryGroupsRecords
                    .filter(x => !x.isNew)
                    .find(
                        x =>
                            (x.getField(DLVGROUPCODE_dg_id).currentValue as string) === DLVGROUPCODE
                    );

                if (deliveryGroupRecord) {
                    if (
                        optimizationDialogState.consider === OptimizationDialogConsiderOption.Area
                    ) {
                        const form =
                            deliveryGroupsSubforms[deliveryGroupRecord.recordIndex.toString()][
                                DLVZONEGROUPS
                            ];
                        if (form) {
                            zoneList.addZones(0, 0);
                            const ZONE_id = form.getColumnByName(
                                ColumnsNamesForDeliveryZonesForGroups.ZONE
                            ).id;
                            const OPPTIMIZATION_id = form.getColumnByName(
                                ColumnsNamesForDeliveryZonesForGroups.OPPTIMIZATION
                            ).id;

                            const records = form.records
                                .filter(x => !x.isNew)
                                .filter(x =>
                                    deliveryGroupRecord.getField(ALLZONES_id).Value.Boolean
                                        ? true
                                        : x.getField(OPPTIMIZATION_id).Value.Boolean
                                );

                            for (let i = 0; i < records.length; ++i) {
                                zoneList.addZones(
                                    records[i].getField(ZONE_id).Value.Int as number,
                                    i + 1
                                );
                            }
                        }
                    } else if (
                        optimizationDialogState.consider === OptimizationDialogConsiderOption.Route
                    ) {
                        const form =
                            deliveryGroupsSubforms[deliveryGroupRecord.recordIndex.toString()][
                                DLVDISTRLINEGROUPS
                            ];
                        if (form) {
                            zoneList.addZones(0, 0);
                            const DISTRLINE_id = form.getColumnByName(
                                ColumnsNamesForDeliveryDistributionRoutesForGroups.DISTRLINE
                            ).id;
                            const OPPTIMIZATION_id = form.getColumnByName(
                                ColumnsNamesForDeliveryDistributionRoutesForGroups.OPPTIMIZATION
                            ).id;

                            const records = form.records
                                .filter(x => !x.isNew)
                                .filter(x =>
                                    deliveryGroupRecord.getField(ALLROUTES_id).Value.Boolean
                                        ? true
                                        : x.getField(OPPTIMIZATION_id).Value.Boolean
                                );

                            for (let i = 0; i < records.length; ++i) {
                                zoneList.addZones(
                                    records[i].getField(DISTRLINE_id).Value.Int as number,
                                    i + 1
                                );
                            }
                        }
                    }
                }

                vehicle.setAllowedzones(zoneList);

                const skillList = new RouteOptimization.SkillList();

                if (optimizationDialogState.constraintsConsideration && deliveryGroupRecord) {
                    const form =
                        deliveryGroupsSubforms[deliveryGroupRecord.recordIndex.toString()][
                            DLVCAPABILITYGROUPS
                        ];
                    if (form) {
                        skillList.addSkills(0, 0);
                        const CAPABILITY_id = form.getColumnByName(
                            ColumnsNamesForDeliveryCapabilitiesForGroups.CAPABILITY
                        ).id;
                        const OPTIMIZATION_id = form.getColumnByName(
                            ColumnsNamesForDeliveryCapabilitiesForGroups.OPTIMIZATION
                        ).id;

                        const records = form.records
                            .filter(x => !x.isNew)
                            .filter(x =>
                                deliveryGroupRecord.getField(ALLCAPABILITIES_id).Value.Boolean
                                    ? true
                                    : x.getField(OPTIMIZATION_id).Value.Boolean
                            );

                        for (let i = 0; i < records.length; ++i) {
                            skillList.addSkills(
                                Number.parseInt(
                                    records[i].getField(CAPABILITY_id).currentValue as string
                                ),
                                i + 1
                            );
                        }
                    }
                }

                vehicle.setSkills(skillList);

                return vehicle;
            })
            .filter(Boolean) as RouteOptimization.Vehicle[])
    );

    // Set request options based on user input
    options.setMetric(
        optimizationDialogState.optimizeByDistance
            ? RouteOptimization.Options.MetricType.DISTANCE
            : RouteOptimization.Options.MetricType.DURATION
    );
    options.setRoundtrip(false);
    options.setCost(0);

    return {
        locations,
        vehicles,
        jobs,
        options
    };
};

export const generateRouteRequestData = async (
    routesForm: Form,
    routes: RouteType[],
    selectedRoutes: RouteType[],
    optimizationDialogState: OptimizationDialogState
): Promise<RequestData> => {
    const locations: Common.Location[] = [];
    const jobs: RouteOptimization.Job[] = [];
    const vehicles: RouteOptimization.Vehicle[] = [];
    const options = new RouteOptimization.Options();

    const routesToProcess = routes.filter(x =>
        optimizationDialogState.runOnlyOnSelected
            ? selectedRoutes.map(y => y.id).includes(x.id)
            : true
    );

    const locationsWarehouses: Common.Location[] = [];
    const locationsDelivery: Common.Location[] = [];

    for (const route of routesToProcess) {
        const startWarehouse = new Common.Location();
        if (route.startMarker) {
            startWarehouse.setLatitude(route.startMarker.lat);
            startWarehouse.setLongitude(route.startMarker.lng);
        }

        const endWarehouse = new Common.Location();
        if (route.endMarker) {
            endWarehouse.setLatitude(route.endMarker.lat);
            endWarehouse.setLongitude(route.endMarker.lng);
        }

        if (
            !locationsWarehouses.find(
                x =>
                    x.getLatitude() === startWarehouse.getLatitude() &&
                    x.getLongitude() === startWarehouse.getLongitude()
            )
        ) {
            if (checkLocation(startWarehouse)) {
                locationsWarehouses.push(startWarehouse);
            }
        }

        if (
            !locationsWarehouses.find(
                x =>
                    x.getLatitude() === endWarehouse.getLatitude() &&
                    x.getLongitude() === endWarehouse.getLongitude()
            )
        ) {
            if (checkLocation(endWarehouse)) {
                locationsWarehouses.push(endWarehouse);
            }
        }

        for (const location of route.locations) {
            if (location.data) {
                const deliveryLocation = new Common.Location();
                const GPSX_id = location.data.parentForm.getColumnByName(GPSX).id;
                const GPSY_id = location.data.parentForm.getColumnByName(GPSY).id;
                deliveryLocation.setLatitude(
                    Number.parseFloat(location.data.getField(GPSX_id).currentValue as string)
                );
                deliveryLocation.setLongitude(
                    Number.parseFloat(location.data.getField(GPSY_id).currentValue as string)
                );

                if (
                    !locationsDelivery.find(
                        x =>
                            x.getLatitude() === deliveryLocation.getLatitude() &&
                            x.getLongitude() === deliveryLocation.getLongitude()
                    )
                ) {
                    if (checkLocation(deliveryLocation)) {
                        locationsDelivery.push(deliveryLocation);
                    } else {
                        await showMissingLocationMessage();
                    }
                }
            }
        }
    }

    locations.push(...locationsWarehouses);

    for (const location of locationsDelivery) {
        if (
            !locations.find(
                x =>
                    x.getLatitude() === location.getLatitude() &&
                    x.getLongitude() === location.getLongitude()
            )
        ) {
            locations.push(location);
        }
    }

    vehicles.push(
        ...(routesToProcess
            .map(x => {
                const vehicle = new RouteOptimization.Vehicle();
                const DLVTRACKNUM_id = routesForm.getColumnByName(
                    ColumnsNamesForRoutes.DLVTRACKNUM
                ).id;
                vehicle.setId(x.data.getField(DLVTRACKNUM_id).currentValue as string);
                vehicle.setDescription('');
                const startLocationIndex = locations.findIndex(
                    y =>
                        y.getLongitude() === x.startMarker.lng &&
                        y.getLatitude() === x.startMarker.lat
                );
                const endLocationIndex = locations.findIndex(
                    y => y.getLongitude() === x.endMarker.lng && y.getLatitude() === x.endMarker.lat
                );
                if (startLocationIndex !== -1) {
                    vehicle.setStartindex(startLocationIndex);
                }
                if (endLocationIndex !== -1) {
                    vehicle.setEndindex(endLocationIndex);
                }
                const DLVTRACK_id = routesForm.getColumnByName(ColumnsNamesForRoutes.DLVTRACK).id;
                const zoneList = new RouteOptimization.ZoneList();
                zoneList.addZones(
                    Number.parseInt(x.data.getField(DLVTRACK_id).currentValue as string),
                    0
                );
                vehicle.setAllowedzones(zoneList);

                return vehicle;
            })
            .filter(Boolean) as RouteOptimization.Vehicle[])
    );

    jobs.push(
        ...routesToProcess
            .map(route => {
                return _.sortBy(route.locations, [
                    location => {
                        const DISTRORDER_id = location.data.parentForm.getColumnByName(
                            ColumnsNamesForLocationsSubform.DISTRORDER
                        ).id;

                        return Number.parseInt(
                            location.data.getField(DISTRORDER_id).currentValue as string
                        );
                    }
                ])
                    .map((location: LocationType, index) => {
                        const job = new RouteOptimization.Job();
                        const KLINE_id = location.data.parentForm.getColumnByName(
                            ColumnsNamesForLocationsSubform.KLINE
                        ).id;
                        const FROMTIME_id = location.data.parentForm.getColumnByName(
                            ColumnsNamesForLocationsSubform.FROMTIME
                        ).id;
                        const TOTIME_id = location.data.parentForm.getColumnByName(
                            ColumnsNamesForLocationsSubform.TOTIME
                        ).id;
                        const UNLOADTIME_id = location.data.parentForm.getColumnByName(
                            ColumnsNamesForLocationsSubform.UNLOADTIME
                        ).id;
                        const DISTRDATE_id = route.data.parentForm.getColumnByName(
                            ColumnsNamesForRoutes.DISTRDATE
                        ).id;

                        if (
                            selectedRoutes.length === 1 &&
                            optimizationDialogState.runOnlyOnSelected &&
                            optimizationDialogState.startLocationId !== undefined
                        ) {
                            const sequence_order =
                                index >= optimizationDialogState.startLocationId
                                    ? optimizationDialogState.startLocationId + 1
                                    : index + 1;

                            job.setSequenceorder(sequence_order);
                        }

                        job.setId(
                            (location.data.getField(KLINE_id).Value.Int as number).toString()
                        );
                        job.setDescription('Delivery location');

                        if (optimizationDialogState.timeWindowConsideration) {
                            const date = route.data.getField(DISTRDATE_id);
                            const fromTime = location.data.getField(FROMTIME_id);
                            const toTime = location.data.getField(TOTIME_id);
                            const unloadTime = location.data.getField(UNLOADTIME_id);

                            addTimeWindowAndServiceToJob(job, date, fromTime, toTime, unloadTime);
                        }

                        const DLVTRACK_id = route.data.parentForm.getColumnByName(
                            ColumnsNamesForRoutes.DLVTRACK
                        ).id;
                        const zoneList = new RouteOptimization.ZoneList();
                        zoneList.addZones(
                            Number.parseInt(
                                route.data.getField(DLVTRACK_id).currentValue as string
                            ),
                            0
                        );
                        job.setZones(zoneList);

                        const GPSX_id = location.data.parentForm.getColumnByName(GPSX).id;
                        const GPSY_id = location.data.parentForm.getColumnByName(GPSY).id;
                        const lat = Number.parseFloat(
                            location.data.getField(GPSX_id).currentValue as string
                        );
                        const lng = Number.parseFloat(
                            location.data.getField(GPSY_id).currentValue as string
                        );
                        const locationIndex = locations.findIndex(
                            y => y.getLongitude() === lng && y.getLatitude() === lat
                        );

                        if (locationIndex === -1) {
                            return false;
                        }
                        job.setLocationindex(locationIndex);

                        return job;
                    })
                    .filter(Boolean) as RouteOptimization.Job[];
            })
            .flat()
    );

    options.setMetric(
        optimizationDialogState.optimizeByDistance
            ? RouteOptimization.Options.MetricType.DISTANCE
            : RouteOptimization.Options.MetricType.DURATION
    );

    return {
        locations,
        vehicles,
        jobs,
        options
    };
};
